import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import * as Cookies from 'js-cookie';
import { ReactComponent as OpportunityIcon } from 'icons/inboxOpportunity.svg';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as PencilEditIcon } from 'icons/edit.svg';
import moment from 'moment-timezone';
import { makeStyles } from '@material-ui/styles';
import Button from 'components/buttons/Button';
import PlusIcon from 'mdi-react/PlusIcon';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import TableSortLabel from 'components/tables/tableSortLabel';
import Tag from 'components/forms/Tag';
// POST MVP import FileDownloadOutlined from 'icons/fileDownloadOutlined';
// POST MVP import ShareOutlined from 'icons/shareOutlined';
import Pagination from 'components/pagination/Pagination';
import { Can, AbilityContext } from 'utils/permissions';
import { formatNumber } from 'utils/report';
import {
  DynamicFilter,
  CampaignsFilter,
  OutletsFilter,
  AccountsFilter,
} from 'components/filters';
import LoadingIcon from 'mdi-react/LoadingIcon';
import DateRangePicker from 'components/forms/DateRangePicker';
import { getMentions } from 'redux/actions/coverageActions';
import { DATE_FORMAT_VERBOSE, DATE_FORMAT_NO_TIME_ZONE, PAGE_SIZE } from 'constants/config';
import { ARTICLE_TYPE_OPTIONS, FOLLOW_OPTIONS } from 'constants/coverage';
import NoResults from 'components/NoResults';
import defaultHitImg from 'images/default_hit.jpg';
import { at, curry, first, get, isEmpty } from 'lodash';
import AddHit from './AddHit';
import Summary from './Summary';
import HideFields from './HideFields';
import CoverageNotesFilter from '../filters/CoverageNotesFilter';

const styles = makeStyles(theme => ({
  filterWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  whiteWrapper: props => ({
    backgroundColor: theme.palette.common.white,
    borderRadius: '0.375rem',
    paddingBottom: (props.mentions && props.mentions.count > PAGE_SIZE) ? '14px' : '0',
  }),
  tagStyle: {
    marginLeft: '0.7rem',
    marginBottom: '0.2rem',
  },
  opportunityIcon: {
    margin: 0,
    height: 24,
    opacity: '1 !important',
    marginLeft: 4,
    marginRight: -2,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.5rem 1rem',
  },
  icon: {
    cursor: 'pointer',
  },
  fileDownloadIcon: {
    marginRight: '1rem',
  },
  plusIcon: {
    marginRight: '0.4rem',
    marginTop: '-0.15rem',
  },
  editButton: {
    visibility: 'hidden',
  },
  editIcon: {
    width: '1rem',
    height: '1rem',
    fill: theme.palette.secondary[900],
  },
  editCell: {},
  row: {
    '&:hover $editCell $editButton': {
      visibility: 'visible',
    },
  },
  cell: {
    color: theme.palette.secondary.text,
  },
  logo: {
    borderRadius: '0.125rem',
    width: '2.25rem',
    height: '2.25rem',
    objectFit: 'contain',
  },
  articleLink: {
    textDecoration: 'underline',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
}));

const FILTER_ACCOUNT = 'account';
const FILTER_CAMPAIGN_NAME = 'campaign_name';
const FILTER_OUTLET = 'outlet';
const FILTER_DATE = 'date';
const FILTER_NOTES = 'note';

const defaultFilters = {
  campaign__id: [],
  account__id: [],
  outlet__id: [],
  start_date: null,
  end_date: null,
  ordering: null,
};


const Coverage = ({ mentions, getMentionsAction }) => {
  const classes = styles({ mentions });
  const [filterParams, setFilterParams] = useState(defaultFilters);
  const [openModal, setOpenModal] = useState(false);
  const [mentionToEdit, setMentionToEdit] = useState(null);
  const [orderState, setOrderState] = useState('asc');
  const showFields = JSON.parse(Cookies.get('showFields') || '{}');
  const sorting = orderState === 'asc' ? 'desc' : 'asc';

  const [fieldsToShow, setFieldsToShow] = useState({
    outlet: {
      show: isEmpty(showFields) || showFields.outlet,
      label: 'Outlet',
      valuePath: 'outlet.name',
      field: 'outlet__name',
    },
    author: {
      show: showFields.author,
      label: 'Author',
      valuePath: 'article_authors',
      field: 'article_authors',
    },
    date: {
      show: isEmpty(showFields) || showFields.date,
      label: 'Date',
      valuePath: 'article_published_at',
      field: 'article_published_at',
      content: value => (first(value) ? moment(first(value)).local().format(DATE_FORMAT_VERBOSE) : ''),
    },
    reach: {
      show: isEmpty(showFields) || showFields.reach,
      label: 'Reach',
      align: 'right',
      valuePath: 'potential_reach',
      field: 'potential_reach',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    uvpm: {
      show: isEmpty(showFields) || showFields.uvpm,
      label: 'UVPM',
      align: 'right',
      valuePath: 'unique_monthly_visitors',
      field: 'unique_monthly_visitors',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    article_type: {
      show: showFields.article_type,
      label: 'Article type',
      valuePath: 'article_type',
      field: 'article_type',
      content: value => ARTICLE_TYPE_OPTIONS.find(option => option.value === value[0])?.label,
    },
    follow_status: {
      show: showFields.follow_status,
      label: 'Follow',
      valuePath: 'follow_status',
      field: 'follow_status',
      content: value => FOLLOW_OPTIONS.find(option => option.value === value[0])?.label,
    },
    domainAuthority: {
      show: showFields.domainAuthority,
      label: 'Domain Authority',
      align: 'right',
      valuePath: 'outlet.domain_authority',
      field: 'outlet__domain_authority',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    type: {
      show: showFields.type,
      label: 'Type',
      valuePath: 'outlet.outlet_type',
      field: 'outlet__outlet_type',
    },
    campaign: {
      show: isEmpty(showFields) || showFields.campaign,
      label: 'Campaign',
      valuePath: 'campaign.name',
      field: 'campaign__name',
      content: value => (first(value) ? <Tag color="gray" label={value} /> : null),
    },
    sequence: {
      show: showFields.sequence,
      label: 'Sequence',
      valuePath: 'sequence.name',
      field: 'sequence__name',
    },
    account: {
      show: isEmpty(showFields) || showFields.account,
      label: 'Account',
      valuePath: 'account.name',
      field: 'account__name',
    },
    opportunity: {
      show: isEmpty(showFields) || showFields.opportunity,
      label: 'Opportunity',
      valuePath: 'opportunity.name',
      field: 'opportunity__title',
    },
    note: {
      show: showFields.note,
      label: 'Notes',
      valuePath: 'note',
      field: 'note',
    },
  });

  useEffect(() => {
    getMentionsAction();
  }, []);

  const toggleModal = () => {
    if (openModal) {
      setMentionToEdit(null);
    }
    setOpenModal(!openModal);
  };

  const ability = useContext(AbilityContext);

  const prepareFilterParams = (payload) => {
    const params = { ...payload };
    if (sorting === 'desc' && payload.ordering) {
      params.ordering = `-${payload.ordering}`;
    }
    params.outlet__id = params.outlet__id.map(l => (l.value)).join(',');
    params.campaign__id = params.campaign__id.map(l => (l.value)).join(',');
    params.account__id = params.account__id.map(l => (l.value)).join(',');
    params.date_after = params.start_date
      ? moment(params.start_date).startOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    params.date_before = params.end_date
      ? moment(params.end_date).endOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    delete params.start_date;
    delete params.end_date;
    return params;
  };

  useEffect(() => {
    const params = prepareFilterParams(filterParams);
    const { date_before: dateBefore, date_after: dateAfter } = params;
    if ((dateBefore && dateAfter) || (!dateBefore && !dateAfter)) {
      getMentionsAction(params);
    }
  }, [filterParams]);


  const handleOrdering = (ordering) => {
    setOrderState(sorting);
    setFilterParams({ ...filterParams, ordering });
  };

  const handleFilterChange = (name, value) => {
    const newState = { ...filterParams, [name]: value };
    setFilterParams(newState);
  };

  const handleChangeDate = (startDate, endDate) => {
    const newState = { ...filterParams, start_date: startDate, end_date: endDate };
    setFilterParams(newState);
  };

  const onResetSelectedFilter = (filter) => {
    const newParams = {};
    switch (filter) {
      case FILTER_ACCOUNT:
        newParams.account__id = [];
        break;
      case FILTER_CAMPAIGN_NAME:
        newParams.campaign__id = [];
        break;
      case FILTER_OUTLET:
        newParams.outlet__id = [];
        break;
      case FILTER_DATE:
        newParams.start_date = null;
        newParams.end_date = null;
        break;
      case FILTER_NOTES:
        newParams.note = null;
        break;
      default:
        break;
    }

    setFilterParams({ ...filterParams, ...newParams });
  };

  const onResetFilters = () => {
    const newState = {
      ...defaultFilters,
    };
    setFilterParams(newState);
  };

  const handleEditClick = (mention) => {
    setMentionToEdit(mention);
    toggleModal();
  };

  const onDeleteFilter = (selectedFilter) => {
    const newParams = {};
    switch (selectedFilter) {
      case FILTER_ACCOUNT:
        newParams.account__id = [];
        break;
      case FILTER_CAMPAIGN_NAME:
        newParams.campaign__id = [];
        break;
      case FILTER_OUTLET:
        newParams.outlet__id = [];
        break;
      case FILTER_DATE:
        newParams.start_date = null;
        newParams.end_date = null;
        break;
      case FILTER_NOTES:
        newParams.note = null;
        break;
      default:
        break;
    }

    setFilterParams({ ...filterParams, ...newParams });
  };

  const curriedHandleFilterChange = curry(handleFilterChange);

  return (
    <div>
      <div className={classes.filterWrapper}>
        <DynamicFilter
          onResetSelectedFilter={onResetSelectedFilter}
          onResetFilters={onResetFilters}
          menuDescription="Show all hits where:"
          options={[
            ...(
              ability.can('see', 'Account') ? [
                {
                  key: FILTER_ACCOUNT,
                  menuLabel: 'Accounts',
                  onDeleteFilter,
                  component: <AccountsFilter
                    value={filterParams.account__id}
                    onChange={curriedHandleFilterChange('account__id')}
                  />,
                },
              ] : []),
              {
                key: FILTER_CAMPAIGN_NAME,
                menuLabel: 'Campaign Name',
                onDeleteFilter,
                component: <CampaignsFilter
                  value={filterParams.campaign__id}
                  onChange={curriedHandleFilterChange('campaign__id')}
                  accountIds={filterParams.account__id}
                  includeAllOption={false}
                />,
              },
              {
                key: FILTER_OUTLET,
                menuLabel: 'Outlets',
                onDeleteFilter,
                component: <OutletsFilter
                  value={filterParams.outlet__id}
                  onChange={curriedHandleFilterChange('outlet__id')}
                />,
              },
              {
                key: FILTER_DATE,
                menuLabel: 'Published date',
                onDeleteFilter,
                component: <DateRangePicker
                  initialStartDate={filterParams.start_date}
                  initialEndDate={filterParams.end_date}
                  onChange={handleChangeDate}
                  minDate={null}
                  maxDate={moment()}
                />,
              },
              {
                key: FILTER_NOTES,
                menuLabel: 'Notes',
                onDeleteFilter,
                component: <CoverageNotesFilter
                  value={filterParams.note}
                  onChange={curriedHandleFilterChange('note')}
                />,
              },
          ]}
        />
        <Can I="edit" a="Coverage">
          <Button
            color="primary"
            variant="contained"
            size="large"
            onClick={toggleModal}
          ><PlusIcon className={classes.plusIcon} />Add coverage
          </Button>
          {openModal && (
            <AddHit
              toggle={toggleModal}
              initialValues={mentionToEdit && {
                id: mentionToEdit.id,
                articleURL: mentionToEdit.article_url,
                title: mentionToEdit.article_title,
                // apply current timezone
                date: mentionToEdit.article_published_at
                  ? moment.utc(mentionToEdit.article_published_at)
                      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone).toDate()
                  : null,
                author: mentionToEdit.article_authors,
                outlet: { label: mentionToEdit.outlet.name, value: mentionToEdit.outlet.id },
                type: mentionToEdit.outlet.outlet_type,
                account: {
                  label: mentionToEdit.account.name,
                  value: mentionToEdit.account.id,
                },
                campaign: {
                  label: get(mentionToEdit, 'campaign.name'),
                  value: get(mentionToEdit, 'campaign.id'),
                },
                sequence: {
                  label: get(mentionToEdit, 'sequence.name'),
                  value: get(mentionToEdit, 'sequence.id'),
                },
                opportunity: get(mentionToEdit, 'opportunity.name') ? {
                  label: get(mentionToEdit, 'opportunity.name'),
                  value: get(mentionToEdit, 'opportunity.id'),
                } : null,
                note: mentionToEdit.note,
                article_type: ARTICLE_TYPE_OPTIONS.find(option => option.value === get(mentionToEdit, 'article_type')) || null,
                follow_status: FOLLOW_OPTIONS.find(option => option.value === get(mentionToEdit, 'follow_status')) || null,
              }}
            />
          )}
        </Can>
      </div>
      <div className={classes.whiteWrapper}>
        {!isEmpty(mentions.results) && (
          <>
            <div className={classes.infoRow}>
              <HideFields
                fieldsToShow={fieldsToShow}
                onFieldsToShowChange={setFieldsToShow}
                cookieKey="coverageShowFields"
              />
              <Summary
                hitsCount={mentions.count}
                totalReach={mentions.total_potential_reach}
                averageReach={mentions.average_potential_reach}
              />
              <div>
                {/* POST MVP
                <FileDownloadOutlined className={classNames(classes.fileDownloadIcon, classes.icon)} />
                <ShareOutlined className={classes.icon} />
                */}
              </div>
            </div>
            <div className="material-table__wrap">
              {mentions.loading ? (
                <div className="loader"><LoadingIcon /></div>
              ) : (
                <>
                  <Table className="volt-table">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell onClick={() => handleOrdering('article_title')}>
                          <TableSortLabel
                            order={orderState}
                            orderBy={filterParams.ordering}
                            property="title"
                          >
                              Title
                          </TableSortLabel>
                        </TableCell>
                        <Can I="edit" a="Coverage">
                          <TableCell />
                        </Can>
                        {Object.values(fieldsToShow).map(field => (
                          field.show && (
                            <TableCell
                              key={field.label}
                              alignRight={field.align === 'right'}
                              onClick={field.field ? () => handleOrdering(field.field) : () => {}}
                              className="sortable truncate-text--200"
                            >
                              <TableSortLabel
                                order={orderState}
                                orderBy={filterParams.ordering}
                                property={field.field}
                              >
                                {field.label}
                              </TableSortLabel>
                            </TableCell>
                          )
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mentions.results.map(mention => (
                        <TableRow key={mention.id} className={classes.row}>
                          <TableCell className={classes.cell}>
                            <img
                              className={classes.logo}
                              src={mention.outlet.logo || defaultHitImg}
                              alt="logo"
                              width={36}
                              height={36}
                            />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <a
                              className={classes.articleLink}
                              href={mention.article_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {mention.article_title}
                            </a>
                          </TableCell>
                          <Can I="edit" a="Coverage">
                            <TableCell className={classNames(classes.cell, classes.editCell)}>
                              <IconButton onClick={() => handleEditClick(mention)} className={classes.editButton}>
                                <PencilEditIcon className={classes.editIcon} />
                              </IconButton>
                            </TableCell>
                          </Can>
                          {Object.values(fieldsToShow).map((field) => {
                            if (field.show && field.label !== 'Opportunity') {
                              return (
                                <TableCell className={classes.cell} alignRight={field.align === 'right'}>
                                  {field.content
                                      ? field.content(at(mention, field.valuePath))
                                      : at(mention, field.valuePath)
                                    }
                                </TableCell>
                              );
                            }
                            if (field.show && field.label === 'Opportunity') {
                              return (
                                <TableCell className={classes.cell} alignRight={field.align === 'right'}>
                                  {!isEmpty(at(mention, field.valuePath)[0]) && (
                                    <Tag
                                      className={classes.tagStyle}
                                      icon={<OpportunityIcon className={classes.opportunityIcon} />}
                                      color="gray"
                                      label={<p className={classes.label}>{at(mention, field.valuePath)}</p>}
                                    />
                                  )}
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    items={mentions}
                    onChangePage={getMentionsAction}
                    params={prepareFilterParams(filterParams)}
                  />
                </>
              )}
            </div>
          </>
        )}

        <NoResults
          loading={mentions.loading}
          results={mentions.results}
        />
      </div>
    </div>
  );
};

Coverage.propTypes = {
  getMentionsAction: PropTypes.func.isRequired,
  mentions: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    results: PropTypes.array,
  }).isRequired,
};

export default compose(connect(
  state => ({
    mentions: state.mentions,
  }),
  {
    getMentionsAction: getMentions,
  },
))(Coverage);
